import { useState, useEffect, useCallback } from "react";
import { Form, Radio, Checkbox, Modal, Input, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { addUpdateDeclaration, getDeclarationInfo } from "../../../../api/request/individualOnboarding";
import { handleApiError } from "../../../../utils/handleApiError.js";
import toast from "react-hot-toast";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";

const Declaration = ({
  onNext,
  onPrevious,
  userId,
  isSubmitted,
  isReadOnly = false,
  canUpdate = true,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [declarationId, setDeclarationId] = useState(null);
  const isDisabled = isSubmitted;
  const relationshipStatus = Form.useWatch("relationshipStatus", form);
  const openAccountChecked = Form.useWatch("openAccount", form);
  const cancelChecked = Form.useWatch("cancel", form);

  const handleLogout = async () => {
    try {
      localStorage.clear();
      navigate("/client");
      setIsModalOpen(false);
    } catch (err) {
      navigate("/");
      setIsModalOpen(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.setFieldsValue({ cancel: false });
  };

  const loadDeclarationInfo = useCallback(async () => {
    try {
      if (!userId) return;
      
      const { data } = await getDeclarationInfo(userId);
      if (data?.success) {
        // Store the id if it exists
        if (data?.data?.id) {
          setDeclarationId(data.data.id);
        }

        // Map boolean isRelationship to string value
        const relationshipStatusValue = data?.data?.isRelationship
          ? "hasRelationship"
          : "noRelationship";

        form.setFieldsValue({
          relationshipStatus: relationshipStatusValue,
          relationship: data?.data?.relationName || "",
          confirmAssetsNotIllegal: data?.data?.confirmAssetsNotIllegal ?? false,
          confirmNotBreachAml: data?.data?.confirmNotBreachAml ?? false,
          confirmNoConviction: data?.data?.confirmNoConviction ?? false,
          confirmFundsTaxed: data?.data?.confirmFundsTaxed ?? false,
          agreeClientAgreement: data?.data?.agreeClientAgreement ?? false,
          declareInfoTrue: data?.data?.declareInfoTrue ?? false,
          confirmVoluntaryRegistration:
            data?.data?.confirmVoluntaryRegistration ?? false,
          undertakeNotifyChanges: data?.data?.undertakeNotifyChanges ?? false,
          // Set checkboxes to true if declaration info exists (assuming they were checked on previous submission)
          openAccount: true,
          privacyPolicy: true,
        });
      }
    } catch (err) {
      console.log("Loading declarationInfo Error", err);
    }
  }, [userId, form]);

  const handleSubmit = async (values) => {
    if (isReadOnly) return;
    try {
      setIsSubmitLoading(true);

      // Build payload with only 2 fields
      const payload = {
        userId: userId || "",
        isRelationship: values.relationshipStatus === "hasRelationship",
        relationName: values.relationship || "",
        confirmAssetsNotIllegal: values.confirmAssetsNotIllegal,
        confirmNotBreachAml: values.confirmNotBreachAml,
        confirmNoConviction: values.confirmNoConviction,
        confirmFundsTaxed: values.confirmFundsTaxed,
        agreeClientAgreement: values.agreeClientAgreement,
        declareInfoTrue: values.declareInfoTrue,
        confirmVoluntaryRegistration: values.confirmVoluntaryRegistration,
        undertakeNotifyChanges: values.undertakeNotifyChanges,
      };

      // Include id only if it exists (for update operations)
      if (declarationId) {
        payload.id = declarationId;
      }

      const { data } = await addUpdateDeclaration(payload);

      if (data?.success) {
        toast.success(
          data?.message || "Declaration information saved successfully"
        );
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (cancelChecked) {
      setIsModalOpen(true);
    }
  }, [cancelChecked]);

  useEffect(() => {
    loadDeclarationInfo();
  }, [loadDeclarationInfo]);

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        className={isReadOnly ? "corporate-review-readonly" : undefined}
        disabled={isDisabled && !isReadOnly}
        onKeyDown={(event) => {
          if (!isReadOnly) return;
          if (event.target.closest?.(".corporate-review-nav")) return;
          event.preventDefault();
        }}
        onFinish={handleSubmit}
        initialValues={{
          relationshipStatus: "noRelationship",
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">Declaration Info</h2>
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              As a regulated entity, we are committed to transparency and
              preventing conflicts of interest. We request that you disclose any
              existing relationships falling into the following categories:
            </p>

            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Direct connections:</strong> Any relationships with CFC,
                its group companies or any of their board of directors,
                including personal or professional ties.
              </p>
              <p className="text-sm text-gray-700">
                <strong>Market connections:</strong> Any relationships with
                capital market institutions, publicly listed companies in the
                UAE, or other entities licensed by the SCA (including their
                boards of directors).
              </p>
            </div>

            <Form.Item
              name="relationshipStatus"
              rules={[
                {
                  required: true,
                  message: "Please select a relationship status",
                },
              ]}
            >
              <Radio.Group disabled={isDisabled}>
                <Radio value="noRelationship">
                  I confirm that I have no relationship with any of the above
                  parties.
                </Radio>
                <Radio value="hasRelationship">
                  I declare that I have the following relationship with.
                </Radio>
              </Radio.Group>
            </Form.Item>
            {relationshipStatus === "hasRelationship" && (
              <Form.Item
                label="Relationship"
                name="relationship"
                rules={[
                  {
                    required: true,
                    message: "Please enter relationship details",
                  },
                ]}
                className="mt-4"
              >
                <Input
                  placeholder="Enter relationship details"
                  disabled={isDisabled}
                />
              </Form.Item>
            )}
          </div>
          <div className="mb-6 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold mb-4">
              Declaration & Acknowledgement
            </h3>
            <div className="space-y-3">
              {[
                [
                  "confirmAssetsNotIllegal",
                  "I confirm that my assets are not derived from illegal activities.",
                ],
                [
                  "confirmNotBreachAml",
                  "I confirm that I will not breach AML regulations.",
                ],
                [
                  "confirmNoConviction",
                  "I confirm that I have no criminal convictions related to financial crime.",
                ],
                [
                  "confirmFundsTaxed",
                  "I confirm that my funds have been taxed in accordance with applicable laws.",
                ],
                [
                  "agreeClientAgreement",
                  "I agree to the Client Agreement and applicable terms.",
                ],
                [
                  "declareInfoTrue",
                  "I declare that the information provided is true and complete.",
                ],
                [
                  "confirmVoluntaryRegistration",
                  "I confirm that I am registering voluntarily and of my own accord.",
                ],
                [
                  "undertakeNotifyChanges",
                  "I undertake to notify UpForex of any material changes to the information provided.",
                ],
              ].map(([name, label]) => (
                <Form.Item
                  key={name}
                  name={name}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("This confirmation is required")
                            ),
                    },
                  ]}
                >
                  <Checkbox disabled={isDisabled}>{label}</Checkbox>
                </Form.Item>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              As responsible, regulated broker it is our duty to assess whether
              our products are appropriate for you.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Based on the information you have provided us about your
              knowledge, experience, and financial standing, we are able to
              offer you an account to trade Contracts for Difference (CFDs).
            </p>
            <p className="text-sm text-red-600 font-semibold mb-4">
              Please remember that CFD trading carries a high level of risk, as
              prices can move rapidly against you, particularly during volatile
              market conditions.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              You will receive an email to the address you have registered with
              us with your account number.
            </p>
          </div>
          <div className="space-y-4">
            <Form.Item
              name="openAccount"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            "Please confirm that you would like to open an account"
                          )
                        ),
                },
              ]}
            >
              <Checkbox disabled={isDisabled}>
                I Would Like To Open An Account And Understand The Risks
                Involved When Trading Cfds.
              </Checkbox>
            </Form.Item>
            {openAccountChecked && (
              <Form.Item
                name="privacyPolicy"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Please confirm that you have read the Privacy Policy and Terms and Conditions"
                            )
                          ),
                  },
                ]}
                className="ml-6"
              >
                <Checkbox disabled={isDisabled}>
                  <span>
                    I confirm that I have read the{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:text-orange-600"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:text-orange-600"
                    >
                      Terms and Conditions
                    </a>
                    .
                  </span>
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item name="cancel" valuePropName="checked">
              <Checkbox disabled={isDisabled}>
                Cancel, I no longer wish to proceed.
              </Checkbox>
            </Form.Item>
          </div>
        </div>
        <div className="flex justify-end gap-4 mt-6">
          {canUpdate && onPrevious && (
            <Button
              onClick={onPrevious}
              size="large"
              disabled={isDisabled}
              className="corporate-review-nav"
            >
              Previous
            </Button>
          )}
          {canUpdate && (
            <LoadableButton
              type="submit"
              lable="Next"
              isLoading={isSubmitLoading}
              loadingLable="Saving..."
              disabled={isDisabled || cancelChecked || isSubmitLoading}
              className="bg-primary text-white text-base font-semibold rounded-lg px-12 py-2 themeHover duration-500"
            />
          )}
        </div>
      </Form>
      {!canUpdate && (
        <div className="flex justify-end gap-4 mt-6">
          {onPrevious && (
            <Button onClick={onPrevious} size="large">
              Previous
            </Button>
          )}
          <Button type="primary" size="large" onClick={onNext}>
            Next
          </Button>
        </div>
      )}
      <Modal
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        centered
        closable={false}
      >
        <div className="text-center py-4">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-2 text-gray-800">
            Cancel, I do not want to proceed
          </h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to cancel the onboarding process.?
          </p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleModalCancel}
              size="large"
              style={{
                borderColor: "#f97316",
                color: "#f97316",
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleLogout}
              size="large"
              className="bg-orange-500 hover:bg-orange-600"
            >
              YES
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Declaration;
